import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Ticket, MessageCircle, Send, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { authFetch, API } from '../utils/api';


const STATUS_STYLES = {
  open: { bg: 'bg-primary/10', text: 'text-primary', label: 'Open' },
  in_progress: { bg: 'bg-yellow-400/10', text: 'text-yellow-400', label: 'In Progress' },
  responded: { bg: 'bg-blue-400/10', text: 'text-blue-400', label: 'Responded' },
  resolved: { bg: 'bg-green-400/10', text: 'text-green-400', label: 'Resolved' },
  closed: { bg: 'bg-muted-foreground/10', text: 'text-muted-foreground', label: 'Closed' },
};

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function TicketConversation({ ticket, onBack, onRefresh }) {
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const messages = ticket.messages || [];
  const st = STATUS_STYLES[ticket.status] || STATUS_STYLES.open;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const handleSend = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      const res = await authFetch(`${API}/api/tickets/${ticket.ticket_id}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ message: reply.trim() }),
      });
      if (res.ok) {
        setReply('');
        await onRefresh();
      }
    } catch {} finally { setSending(false); }
  };

  return (
    <div data-testid="ticket-conversation" className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 px-5 py-3 border-b border-white/5">
        <button
          data-testid="ticket-conv-back-btn"
          onClick={onBack}
          className="p-1 hover:bg-white/10 rounded-full transition-colors"
        >
          <ArrowLeft className="w-4 h-4 text-muted-foreground" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium truncate">{ticket.subject}</p>
            <span className={`text-[8px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${st.bg} ${st.text}`}>{st.label}</span>
          </div>
          <span className="text-[9px] text-muted-foreground/50 font-mono">{ticket.ticket_id}</span>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-5 py-3 space-y-3">
        {messages.map((msg, i) => {
          const isAdmin = msg.from === 'admin';
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              className={`flex ${isAdmin ? 'justify-start' : 'justify-end'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 ${
                  isAdmin
                    ? 'bg-primary/10 border border-primary/15'
                    : 'bg-white/[0.06] border border-white/5'
                }`}
                data-testid={`user-message-${i}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[9px] font-medium ${isAdmin ? 'text-primary' : 'text-muted-foreground'}`}>
                    {isAdmin ? 'Response from Team Swell' : msg.name || 'You'}
                  </span>
                  <span className="text-[8px] text-muted-foreground/40">{timeAgo(msg.created_at)}</span>
                </div>
                <p className="text-xs leading-relaxed text-foreground/80">{msg.message}</p>
              </div>
            </motion.div>
          );
        })}
        {messages.length === 0 && (
          <div className="text-center py-8">
            <p className="text-[10px] text-muted-foreground/40">No messages yet</p>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Reply input */}
      {ticket.status !== 'closed' && (
        <div className="flex gap-2 px-5 py-3 border-t border-white/5">
          <input
            data-testid="user-reply-input"
            type="text"
            value={reply}
            onChange={e => setReply(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSend()}
            placeholder="Type a reply..."
            className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary/50 transition-colors placeholder:text-muted-foreground/30"
          />
          <button
            data-testid="user-send-btn"
            onClick={handleSend}
            disabled={sending || !reply.trim()}
            className="px-3 py-2.5 rounded-xl bg-primary/20 text-primary border border-primary/30 hover:bg-primary/30 transition-colors disabled:opacity-40"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      )}
      {ticket.status === 'closed' && (
        <div className="px-5 py-3 border-t border-white/5 text-center">
          <p className="text-[10px] text-muted-foreground/40">This ticket is closed</p>
        </div>
      )}
    </div>
  );
}

export default function Tickets() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [activeTicket, setActiveTicket] = useState(null);

  const loadTickets = async () => {
    try {
      const res = await authFetch(`${API}/api/tickets`);
      if (res.ok) {
        const data = await res.json();
        setTickets(data.tickets || []);
      }
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { loadTickets(); }, []);

  const openTicket = async (ticketId) => {
    try {
      const res = await authFetch(`${API}/api/tickets/${ticketId}`);
      if (res.ok) {
        const data = await res.json();
        setActiveTicket(data);
      }
    } catch {}
  };

  const refreshActiveTicket = async () => {
    if (!activeTicket) return;
    await openTicket(activeTicket.ticket_id);
    await loadTickets();
  };

  const openCount = tickets.filter(t => t.status === 'open' || t.status === 'in_progress' || t.status === 'responded').length;
  const filtered = filter === 'all' ? tickets : tickets.filter(t => t.status === filter);

  // Show conversation view
  if (activeTicket) {
    return (
      <div className="min-h-screen flex items-start justify-center px-4 py-8 md:py-12 relative z-10">
        <div className="w-full max-w-md bg-bg-paper/95 backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl overflow-hidden min-h-[85vh] max-h-[90vh] flex flex-col">
          <TicketConversation
            ticket={activeTicket}
            onBack={() => { setActiveTicket(null); loadTickets(); }}
            onRefresh={refreshActiveTicket}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg flex items-start justify-center px-4 py-8 md:py-12">
      <div className="w-full max-w-md bg-bg-paper/95 backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl overflow-hidden min-h-[85vh] flex flex-col" data-testid="tickets-page">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/5">
          <button data-testid="tickets-back-btn" onClick={() => navigate('/profile')} className="p-1.5 hover:bg-white/10 rounded-full transition-colors">
            <ChevronLeft className="w-5 h-5 text-muted-foreground" />
          </button>
          <div className="flex-1">
            <h1 className="font-heading text-base font-semibold">My Tickets</h1>
          </div>
          {openCount > 0 && (
            <span className="text-[9px] bg-primary/15 text-primary font-bold px-2 py-0.5 rounded-full">{openCount} open</span>
          )}
        </div>

        <div className="px-5 py-4 max-h-[calc(100vh-200px)] overflow-y-auto">
          {/* Help hint */}
          <div className="bg-primary/5 border border-primary/15 rounded-2xl p-3 mb-4 flex items-start gap-3">
            <MessageCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Click a ticket to view the conversation. <button onClick={() => navigate('/chat')} className="text-primary font-medium hover:underline">Chat with Swell</button> for quick help.
              </p>
            </div>
          </div>

          {/* Filters */}
          {tickets.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {['all', 'open', 'responded', 'in_progress', 'closed'].map(f => (
                <button
                  key={f}
                  data-testid={`tickets-filter-${f}`}
                  onClick={() => setFilter(f)}
                  className={`text-[10px] px-3 py-1.5 rounded-full transition-colors ${
                    filter === f ? 'bg-white/10 text-white' : 'text-muted-foreground hover:text-white'
                  }`}
                >
                  {f === 'all' ? 'All' : f === 'in_progress' ? 'In Progress' : f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          )}

          {/* Ticket List */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="flex space-x-2">
                <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
                <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
                <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
              </div>
            </div>
          ) : filtered.length > 0 ? (
            <div className="space-y-3">
              {filtered.map((ticket, i) => {
                const st = STATUS_STYLES[ticket.status] || STATUS_STYLES.open;
                const lastMsg = (ticket.messages || []).slice(-1)[0];
                const hasAdminReply = lastMsg?.from === 'admin';

                return (
                  <motion.button
                    key={ticket.ticket_id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: i * 0.05 }}
                    onClick={() => openTicket(ticket.ticket_id)}
                    className={`w-full text-left bg-white/3 border rounded-2xl p-4 transition-colors ${
                      hasAdminReply ? 'border-primary/15 bg-primary/[0.03]' : 'border-white/5 hover:border-white/10'
                    }`}
                    data-testid={`ticket-${ticket.ticket_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <Ticket className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-medium truncate">{ticket.subject}</p>
                          <span className={`text-[9px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${st.bg} ${st.text}`}>{st.label}</span>
                          {hasAdminReply && (
                            <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                          )}
                        </div>
                        {lastMsg && (
                          <p className="text-[10px] text-muted-foreground/60 truncate mb-1">
                            {lastMsg.from === 'admin' ? 'Team Swell: ' : 'You: '}{lastMsg.message}
                          </p>
                        )}
                        <div className="flex items-center gap-3 text-[9px] text-muted-foreground/60">
                          <span className="font-mono">{ticket.ticket_id}</span>
                          <span>{timeAgo(ticket.updated_at || ticket.created_at)}</span>
                          <span>{(ticket.messages || []).length} msg</span>
                          {ticket.category !== 'general' && (
                            <span className="bg-white/5 px-1.5 py-0.5 rounded">{ticket.category}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          ) : tickets.length > 0 ? (
            <div className="text-center py-12">
              <p className="text-xs text-muted-foreground">no {filter} tickets</p>
            </div>
          ) : (
            <div className="text-center py-12">
              <Ticket className="w-8 h-8 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">no tickets yet</p>
              <p className="text-[10px] text-muted-foreground/60 mt-1 mb-4">chat with Swell if you need help</p>
              <button
                data-testid="tickets-chat-swell"
                onClick={() => navigate('/chat')}
                className="text-xs bg-primary text-black font-bold px-5 py-2 rounded-xl hover:opacity-90 transition-opacity inline-flex items-center gap-1.5"
              >
                <MessageCircle className="w-3.5 h-3.5" /> Message Swell
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
